import { uniq, omit } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import * as yup from "yup";

import { parseOxygenDateRange } from "@/react/datetime";
import Debug from "@/react/debug";
import LabeledInput from "@/react/components/LabeledInput";
import LabeledDateSelector from "@/react/components/LabeledDateSelector";
import LabeledRadioGroup from "@/react/components/LabeledRadioGroup";
import PageTitle from "@/react/components/PageTitle";
import { DataTable, useDataTable } from "@/react/components/table/useDataTable";
import { StyledTabs, BadgedTab } from "@/react/components/Tabs";
import PageContentContainer from "@/react/features/styled/PageContentContainer";
import PageLayout from "@/react/features/styled/PageLayout";
import { FixedWidthContainer } from "@/react/features/styled/WidthContainers";
import { errorNotification } from "@/react/notifications";
import {
  DEFAULT_AUTO_ID_KEY,
  extractPrefix,
} from "@/react/services/api/transforms";

import {
  useGetBouncesQuery,
  useGetBlocksQuery,
  useGetSpamQuery,
  useUnsuppressBouncesMutation,
  useUnsuppressBlocksMutation,
  useUnsuppressSpamMutation,
  handleUnsuppressResponse,
  useLazyGetBouncesQuery,
  useLazyGetBlocksQuery,
  useLazyGetSpamQuery,
} from "./api";
import columnDefinition from "./columnDefinition";
import {
  EmailSearchMethods,
  MAX_PAGE_SIZE,
  NO_DATA,
  RADIO_INFO_MESSAGE,
  TABLE_KEYS,
} from "./constants";
import schema from "./schema";
import {
  DateSelectionContainer,
  EmailEntryContainer,
  EmailSearchContainer,
  SearchMethodRadioButton,
} from "./styled";
import { useQueryOptions } from "./useQueryOptions";
import { useTabsManagement } from "./useTabsManagement";

const bouncesColumns = columnDefinition();
const blocksColumns = columnDefinition();
const spamColumns = columnDefinition();

const DATA_KEY = schema.fields[DEFAULT_AUTO_ID_KEY].spec.key;
const backgroundQueryChecker = Object.freeze(
  (data) => data?.length === MAX_PAGE_SIZE
);

const staticTableArgs = Object.freeze({
  schema: { definition: schema, key: DATA_KEY },
  mutationsFilter: { delete: true },
  tableOptions: {
    dynamicColumnResizing: true,
    clearMutationsOnDataChange: false,
  },
});

const SEARCH_METHOD_OPTIONS = Object.values(EmailSearchMethods);

const EmailSuppressionPage = () => {
  const today = new Date();
  const todayRange = parseOxygenDateRange({
    dateTime: { startDate: today, endDate: today },
  });

  const [tableDisplayed, setTableDisplayed] = useState(TABLE_KEYS.BOUNCES);
  const [enteredEmail, setEnteredEmail] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedDate, setSelectedDate] = useState(todayRange);
  const [emailSearchMethod, setEmailSearchMethod] = useState(
    EmailSearchMethods.EXACT
  );

  const options = useQueryOptions(
    selectedDate,
    0,
    emailSearchMethod === EmailSearchMethods.CONTAINS &&
      selectedEmail?.length > 0
      ? `*${selectedEmail}*`
      : selectedEmail,
    emailSearchMethod,
    todayRange
  );
  // Same options as the main query, but with pagination settings removed
  const backgroundQueryArgument = omit(options, ["page", "size"]);

  const bouncesQueryHookResult = useGetBouncesQuery(options);
  const [bouncesBackgroundTrigger, bouncesBackgroundHookResult] =
    useLazyGetBouncesQuery();
  const blocksQueryHookResult = useGetBlocksQuery(options);
  const [blocksBackgroundTrigger, blocksBackgroundHookResult] =
    useLazyGetBlocksQuery();
  const spamQueryHookResult = useGetSpamQuery(options);
  const [spamBackgroundTrigger, spamBackgroundHookResult] =
    useLazyGetSpamQuery();

  const [unsuppressBounces, { isLoading: isBouncesUnsuppressing }] =
    useUnsuppressBouncesMutation();
  const [unsuppressBlocks, { isLoading: isBlocksUnsuppressing }] =
    useUnsuppressBlocksMutation();
  const [unsuppressSpam, { isLoading: isSpamUnsuppressing }] =
    useUnsuppressSpamMutation();

  const {
    data: bouncesData,
    props: bouncesTableProps,
    isLoading: isLoadingBounces,
    isDirty: isDirtyBounces,
    isEmpty: isEmptyBounces,
    isInactive: isInactiveBounces,
  } = useDataTable({
    title: "Bounces Table",
    queryHookResult: bouncesQueryHookResult,
    columns: bouncesColumns,
    isLoadingFn: (isFetching) => isFetching || isBouncesUnsuppressing,
    backgroundQuery: {
      trigger: bouncesBackgroundTrigger,
      queryHookResults: bouncesBackgroundHookResult,
      shouldRunQuery: backgroundQueryChecker,
      queryArg: backgroundQueryArgument,
    },
    ...staticTableArgs,
  });

  const {
    data: blocksData,
    props: blocksTableProps,
    isLoading: isLoadingBlocks,
    isDirty: isDirtyBlocks,
    isEmpty: isEmptyBlocks,
    isInactive: isInactiveBlocks,
  } = useDataTable({
    title: "Blocks Table",
    queryHookResult: blocksQueryHookResult,
    columns: blocksColumns,
    isLoadingFn: (isFetching) => isFetching || isBlocksUnsuppressing,
    backgroundQuery: {
      trigger: blocksBackgroundTrigger,
      queryHookResults: blocksBackgroundHookResult,
      shouldRunQuery: backgroundQueryChecker,
      queryArg: backgroundQueryArgument,
    },
    ...staticTableArgs,
  });

  const {
    data: spamData,
    props: spamTableProps,
    isLoading: isLoadingSpam,
    isDirty: isDirtySpam,
    isEmpty: isEmptySpam,
    isInactive: isInactiveSpam,
  } = useDataTable({
    title: "Spam Table",
    queryHookResult: spamQueryHookResult,
    columns: spamColumns,
    isLoadingFn: (isFetching) => isFetching || isSpamUnsuppressing,
    backgroundQuery: {
      trigger: spamBackgroundTrigger,
      queryHookResults: spamBackgroundHookResult,
      shouldRunQuery: backgroundQueryChecker,
      queryArg: backgroundQueryArgument,
    },
    ...staticTableArgs,
  });

  const tables = useMemo(() => {
    return {
      [TABLE_KEYS.BOUNCES]: {
        name: "Bounces",
        unsuppress: unsuppressBounces,
        mutations: bouncesTableProps.mutations,
        hasData: bouncesData?.length > 0 || isDirtyBounces,
        dataSize: bouncesData?.length,
        isLoading: isLoadingBounces,
        isEmptyInactive: isEmptyBounces && isInactiveBounces,
      },
      [TABLE_KEYS.BLOCKS]: {
        name: "Blocks",
        unsuppress: unsuppressBlocks,
        mutations: blocksTableProps.mutations,
        hasData: blocksData?.length > 0 || isDirtyBlocks,
        dataSize: blocksData?.length,
        isLoading: isLoadingBlocks,
        isEmptyInactive: isEmptyBlocks && isInactiveBlocks,
      },
      [TABLE_KEYS.SPAM]: {
        name: "Spam Reports",
        unsuppress: unsuppressSpam,
        mutations: spamTableProps.mutations,
        hasData: spamData?.length > 0 || isDirtySpam,
        dataSize: spamData?.length,
        isLoading: isLoadingSpam,
        isEmptyInactive: isEmptySpam && isInactiveSpam,
      },
    };
  }, [
    unsuppressBounces,
    unsuppressBlocks,
    unsuppressSpam,
    bouncesData,
    blocksData,
    spamData,
    isLoadingBounces,
    isLoadingBlocks,
    isLoadingSpam,
    bouncesTableProps,
    blocksTableProps,
    spamTableProps,
  ]);

  useTabsManagement(tables, tableDisplayed, setTableDisplayed);

  // When the user selects to search for the entered email,
  // then we apply the same value to the 'selectedEmail', which triggers the search
  const searchEmail = useCallback(() => {
    const cleanedEmail = enteredEmail?.trim();
    Debug.log(
      `Should search for email '${cleanedEmail}', method '${emailSearchMethod}'`
    );
    setSelectedEmail(cleanedEmail);
  }, [enteredEmail, emailSearchMethod]);

  // The common handler between all the tables to unsuppress selected emails
  const unSuppressEmails = useCallback(() => {
    Debug.log(`Un-suppress emails for ${tableDisplayed}`);
    if (tableDisplayed === NO_DATA) {
      Debug.error("Should not try to unsuppress when we have no data");
      return;
    }

    // Assume the table displayed is the one we want to deal with
    // This is logical because the Save button is only shown for the table when it is displayed
    const table = tables[tableDisplayed];
    const tableMutations = table.mutations;
    // The id key for each row contains the email address followed by a uuid,
    // so we need to extract the email part and remove duplicates
    // An email address can appear in more than one entry,
    // but it only makes sense to include it once in the API call to unsuppress it
    table
      .unsuppress(
        uniq(Array.from(tableMutations.rowsToDelete.keys().map(extractPrefix)))
      )
      .unwrap()
      .then(
        handleUnsuppressResponse({
          rowsToDelete: tableMutations.rowsToDelete,
          setRowsToDelete: tableMutations.setRowsToDelete,
          setData: tableMutations.setData,
          dataKey: DATA_KEY,
        })
      )
      .catch(errorNotification);
  }, [tables, tableDisplayed]);

  const searchIgnoresDate = emailSearchMethod === EmailSearchMethods.EXACT;

  const emailValidator = (value) =>
    value ? yup.string().email().isValidSync(value.trim()) : true;

  return (
    <PageLayout>
      <PageContentContainer>
        <PageTitle>Email Suppression</PageTitle>
        <EmailSearchContainer>
          <EmailEntryContainer>
            <LabeledInput
              prompt={"Address"}
              placeholder={"abc@example.com"}
              value={enteredEmail ?? ""}
              setValue={setEnteredEmail}
              validator={
                emailSearchMethod === EmailSearchMethods.EXACT
                  ? (event) => emailValidator(event.target.value)
                  : undefined
              }
              allowInvalidInput
              hasError={
                emailSearchMethod === EmailSearchMethods.EXACT &&
                !emailValidator(enteredEmail)
              }
              submitOnEnter
              includeButton
              buttonText={"Search"}
              onSubmit={searchEmail}
              clearable
              /*
               * This runs the search again without filtering by a specific email.
               * When clearing, the 'enteredEmail' will change, but not quick enough before the search.
               * That's why we use an additional function here, to specifically set
               * the 'selectedEmail' to empty, which triggers the new search
               */
              onClear={() => setSelectedEmail("")}
            />
          </EmailEntryContainer>
          <FixedWidthContainer>
            <LabeledRadioGroup
              prompt={"Search Method"}
              name={"emailSearchMethod"}
              value={emailSearchMethod}
              onChange={(value) => setEmailSearchMethod(value)}
              options={SEARCH_METHOD_OPTIONS}
              isHorizontal
              renderRadioButton={(option) => (
                <SearchMethodRadioButton
                  key={option}
                  name={option}
                  value={option}
                  label={option}
                  infoBoxText={RADIO_INFO_MESSAGE[option]}
                />
              )}
            />
          </FixedWidthContainer>
          <DateSelectionContainer isDisabled={searchIgnoresDate}>
            <LabeledDateSelector
              prompt={"Date"}
              placeholder={"Choose date(s)"}
              value={selectedDate}
              setValue={setSelectedDate}
              defaultValue={todayRange}
              clearButtonLabel={"Today"}
              isDisabled={searchIgnoresDate}
            />
          </DateSelectionContainer>
        </EmailSearchContainer>
        <StyledTabs>
          {Object.keys(tables).map((tableKey) => {
            const table = tables[tableKey];
            const disabled = !table.hasData;
            return (
              <BadgedTab
                key={tableKey}
                title={table.name}
                count={table.dataSize}
                onClick={() => setTableDisplayed(tableKey)}
                isActive={tableDisplayed === tableKey}
                isDisabled={disabled}
                hideBadge={disabled}
              />
            );
          })}
        </StyledTabs>
        {tableDisplayed === TABLE_KEYS.BOUNCES && (
          <DataTable onSave={unSuppressEmails} {...bouncesTableProps} />
        )}
        {tableDisplayed === TABLE_KEYS.BLOCKS && (
          <DataTable onSave={unSuppressEmails} {...blocksTableProps} />
        )}
        {tableDisplayed === TABLE_KEYS.SPAM && (
          <DataTable onSave={unSuppressEmails} {...spamTableProps} />
        )}
        {tableDisplayed === NO_DATA && <h6>No results found</h6>}
      </PageContentContainer>
    </PageLayout>
  );
};

export default EmailSuppressionPage;
